import React from 'react';
import { AbsoluteFill } from 'remotion';
import type { ProjectModel } from '@video-studio/shared';
import { TekstLaag } from './TekstLaag.js';
import { AfbeeldingLaag } from './AfbeeldingLaag.js';
import { VideoLaag } from './VideoLaag.js';
import { AudioLaag } from './AudioLaag.js';
import { ProjectAchtergrond } from './ProjectAchtergrond.js';

/**
 * Input voor de compositie.
 *
 * `assetUrls` is de enige brug tussen het projectmodel en de werkelijkheid van
 * bestanden. Het projectmodel bevat uitsluitend asset-id's; de URL's worden
 * door de aanroeper bepaald (de webapp via de API van dezelfde origin, de
 * worker via absolute URL's). Daardoor is er geen tweede waarheid over media.
 */
export interface CompositieProps {
  project: ProjectModel;
  /** assetId -> URL die de renderer kan laden. */
  assetUrls: Record<string, string>;
  /**
   * Duur in frames. Wordt door de aanroeper berekend met
   * `berekenDuurInFrames` uit @video-studio/shared, zodat de compositie en de
   * tijdlijn in de webapp exact hetzelfde tellen.
   */
  duurInFrames?: number;
  /** Optionele fps van de bron per asset, voor de trimomrekening. */
  assetFps?: Record<string, number | null>;
  /** Basistrack die als achtergrond dient (fase 2+); anders losse videolagen. */
  basistrackUrl?: string;
  [key: string]: unknown;
}

/**
 * De enige compositie van de applicatie.
 *
 * Preview en export gebruiken exact dit component en exact dezelfde
 * tijdlijnwiskunde uit @video-studio/shared. Er is geen tweede implementatie
 * van de montage.
 */
export const VideoStudioCompositie: React.FC<CompositieProps> = ({
  project,
  assetUrls,
  assetFps,
  basistrackUrl,
}) => {
  return (
    <AbsoluteFill style={{ backgroundColor: project.achtergrond.kleur }}>
      <ProjectAchtergrond project={project} basistrackUrl={basistrackUrl} />
      {project.tracks.map((track) => {
        if (track.verborgen) return null;
        if (track.type === 'audio') return null;
        const items = [...track.items].sort((a, b) => a.startFrame - b.startFrame);
        return (
          <AbsoluteFill key={track.id}>
            {items.map((item) => {
              if (item.soort === 'video') {
                return (
                  <VideoLaag
                    key={item.id}
                    item={item}
                    project={project}
                    assetUrls={assetUrls}
                    bronFps={assetFps?.[item.assetId] ?? null}
                  />
                );
              }
              if (item.soort === 'afbeelding') {
                return (
                  <AfbeeldingLaag
                    key={item.id}
                    item={item}
                    project={project}
                    assetUrls={assetUrls}
                  />
                );
              }
              if (item.soort === 'tekst') {
                return <TekstLaag key={item.id} item={item} project={project} />;
              }
              return null;
            })}
          </AbsoluteFill>
        );
      })}
      {project.tracks
        .filter((t) => t.type === 'audio' && !t.gedempt && !t.verborgen)
        .map((track) =>
          [...track.items]
            .sort((a, b) => a.startFrame - b.startFrame)
            .map((item) =>
              item.soort === 'audio' ? (
                <AudioLaag key={item.id} item={item} project={project} assetUrls={assetUrls} />
              ) : null,
            ),
        )}
    </AbsoluteFill>
  );
};
